/* OrderLineExpenseAssociationCard.tsx
    Une card affichant l'association des lignes de commande avec les dépenses
    Utilise le composant GenericCrudCard pour lister les lignes de commandes et créer une dépense depuis une ligne de commande
    Utilise l'API correspondante pour les opérations de données
*/

import React from 'react';
import { useNavigate } from 'react-router-dom';
import * as Tooltip from '@radix-ui/react-tooltip';
import { GenericCrudCard } from 'common';
import { OrderLineData, OrderVersionData, orderLineApi } from 'features/orders';
import {
	faBox,
	faCircleCheck,
	faHourglassHalf,
	faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

const EXPENSE_TYPE_LABELS: Record<string, string> = {
	PROJECT: 'Affaire',
	OPERATIONAL: 'Exploitation',
	CAPITAL: 'Investissement',
	Affaire: 'Affaire',
	Exploitation: 'Exploitation',
	Investissement: 'Investissement',
};

const formatCurrency = (value: number): string =>
	new Intl.NumberFormat('fr-FR', {
		style: 'currency',
		currency: 'EUR',
	}).format(value);

const expenseTypeLabel = (value?: string): string =>
	value ? (EXPENSE_TYPE_LABELS[value] ?? value) : '';

interface OrderLineExpenseAssociationCardProps {
	size?: 'xs' | 'sm' | 'md' | 'lg';
	returnPath?: string;
	returnLabel?: string;
	returnIcon?: IconDefinition;
	returnInvert?: boolean;
	className?: string;
	invoiceId?: number;
	supplierId?: number;
}

// OrderLineExpenseAssociationCard component
export const OrderLineExpenseAssociationCard: React.FC<
	OrderLineExpenseAssociationCardProps
> = ({
	size = 'xs',
	returnPath,
	returnLabel,
	returnIcon,
	returnInvert = false,
	className,
	invoiceId,
	supplierId,
}) => {
	const navigate = useNavigate();
	// Détermination si on utilise la version étendue (size !== 'sm')
	const extended = true;

	// Définition des colonnes du tableau
	const columns = [
		// Colonne "ID" uniquement si extended
		...(extended
			? [
					{
						label: 'ID',
						field: 'id',
						linkUrl: (a: OrderLineData) => `/app/orderLines/${a.id}/edit`,
						render: (a: OrderLineData) => a.id,
					},
				]
			: []),
		// Colonne Commande uniquement si extended
		{
			label: 'Commande',
			field: 'orderVersion.businessOrder.referenceNumber',
			linkUrl: (a: OrderLineData) =>
				a.orderVersion?.id
					? `/app/orderVersions/${a.orderVersion.id}/manage`
					: '',
			render: (a: OrderLineData) =>
				(a.orderVersion?.businessOrder?.referenceNumber ?? '') +
				' ' +
				(a.orderVersion?.versionIndex ?? ''),
		},
		/*
		// Colonne Service
		{
			label: 'Service',
			field: 'service',
			render: (a: OrderLineData) => a.service?.label ?? '',
		},
        // Colonne Produit
		{
			label: 'Produit',
			field: 'product',
			render: (a: OrderLineData) => a.product?.label ?? '',
		},
        */
		// Colonne libellé
		{
			label: 'Libellé',
			field: 'label',
			render: (a: OrderLineData) => a.label ?? '',
		},
		// Type de dépense
		{
			label: 'Type',
			field: 'expenseType',
			render: (a: OrderLineData) => expenseTypeLabel(a.expenseType),
		},
		// Code
		{
			label: 'Code',
			field: 'accountingCode',
			render: (a: OrderLineData) => a.accountingCode ?? '',
		},
		/*
		// Colonne "Livré" au format jour/mois/année
		{
			label: 'Livré le',
			field: 'deliveredAt',
			filterType: 'date',
			render: (a: OrderLineData) =>
				a.deliveredAt
					? new Date(a.deliveredAt || '').toLocaleDateString('fr-FR')
					: '—',
		},*/
		// Colonne Livré
		{
			label: 'Livré',
			field: 'deliveredPercentage',
			render: (a: OrderLineData) =>
				a.deliveredPercent != null ? `${a.deliveredPercent}%` : '',
		},
		// Colonne Total HT
		{
			label: 'Total HT',
			field: 'totalExclTax',
			render: (a: OrderLineData) =>
				new Intl.NumberFormat('fr-FR', {
					style: 'currency',
					currency: 'EUR',
				}).format(Number(a.totalExclTax ?? 0)),
		},
		// Colonne Total TTC
		{
			label: 'Total TTC',
			field: 'totalExclTax',
			render: (a: OrderLineData) =>
				new Intl.NumberFormat('fr-FR', {
					style: 'currency',
					currency: 'EUR',
				}).format(Number((a.totalExclTax ?? 0) * (1 + (a.taxRate?.rate ?? 0)))),
		},
		// Colonne Total HT des dépenses / Total HT de la ligne de commande
		{
			label: 'Comptabilisé (HT)',
			field: 'totalSpent',
			headerTooltip:
				'On ne compte que les dépenses associées à une facture fournisseur non annulée.',
			render: (a: OrderLineData) => {
				const totalSpent = a.totalSpent ?? 0;
				const totalExclTax = a.totalExclTax ?? 0;
				if (totalExclTax <= 0) return '—';
				const spentCents = Math.round(totalSpent * 100);
				const totalCents = Math.round(totalExclTax * 100);
				const deliveredCents = Math.round(
					(totalCents * (a.deliveredPercent ?? 0)) / 100
				);
				const overTotal = spentCents > totalCents;
				const overDelivered = spentCents > deliveredCents;
				const icon =
					overTotal || overDelivered
						? faTriangleExclamation
						: spentCents === totalCents
							? faCircleCheck
							: faHourglassHalf;
				const iconClass =
					overTotal || overDelivered
						? 'text-amber-500'
						: spentCents === totalCents
							? 'text-green-600'
							: 'text-gray-400';
				const iconTooltip =
					overTotal && overDelivered
						? 'Le comptabilisé dépasse le total de la ligne, et le paiement dépasse le livré.'
						: overTotal
							? 'Le comptabilisé dépasse le total de la ligne.'
							: overDelivered
								? 'Le paiement dépasse le pourcentage livré.'
								: spentCents === totalCents
									? 'La ligne est entièrement comptabilisée.'
									: 'La ligne n’est pas encore entièrement comptabilisée.';
				return (
					<span className="inline-flex items-center gap-2">
						<Tooltip.Provider delayDuration={200}>
							<Tooltip.Root>
								<Tooltip.Trigger asChild>
									<span className="inline-flex cursor-help">
										<FontAwesomeIcon
											icon={icon}
											className={`w-4 h-4 ${iconClass}`}
										/>
									</span>
								</Tooltip.Trigger>
								<Tooltip.Content
									side="top"
									align="start"
									className="z-50 max-w-xs rounded bg-gray-800 px-2 py-1 text-xs text-white"
								>
									{iconTooltip}
									<Tooltip.Arrow className="fill-gray-800" />
								</Tooltip.Content>
							</Tooltip.Root>
						</Tooltip.Provider>
						<span>
							{((totalSpent / totalExclTax) * 100).toFixed(0)}% (
							{formatCurrency(totalSpent)} / {formatCurrency(totalExclTax)})
						</span>
					</span>
				);
			},
			exportValue: (a: OrderLineData): string => {
				const totalSpent = a.totalSpent ?? 0;
				const totalExclTax = a.totalExclTax ?? 0;
				if (totalExclTax <= 0) return '';
				return `${((totalSpent / totalExclTax) * 100).toFixed(0)}% (${formatCurrency(totalSpent)} / ${formatCurrency(totalExclTax)})`;
			},
		},
		// Colonne fournisseur
		{
			label: 'Fournisseur',
			field: 'a.orderVersion.businessOrder.tiers.label',
			render: (a: OrderLineData) =>
				a.orderVersion?.businessOrder?.tiers?.label ?? '',
		},
	];

	const gotoNewExpenseFromQuoteLine = (id: number) => {
		// Navigation vers /app/expenses/new?orderLineId={id} avec un return page vers /app/supplierInvoices/invoiceId/edit
		navigate(
			`/app/expenses/new?orderLineId=${id}&supplierInvoiceId=${invoiceId}`
		);
	};

	return (
		<GenericCrudCard<OrderLineData>
			queryKey="orderLines"
			title={
				size === 'sm' || size === 'xs'
					? 'Lignes de commande'
					: 'Gestion des lignes de commande'
			}
			fetchData={(token, options) =>
				orderLineApi
					.listPaginated(token, {
						...options,
						sort: options?.sort || 'label',
						order: options?.order || 'ASC',
						filters: {
							...options?.filters,
							'orderVersion.businessOrder.tiers.id': supplierId
								? String(supplierId)
								: '',
						},
					})
					.then((res) => {
						const lines = res?.data ?? [];

						// 1. Déterminer la dernière orderVersion non CANCELLED par businessOrder
						const lastVersionByOrderId: Record<number, OrderVersionData> = {};

						lines.forEach((line) => {
							const ov = line.orderVersion;
							const bo = ov?.businessOrder;

							if (!line.id || !ov || !bo || ov.status === 'CANCELED') return;

							const current = lastVersionByOrderId[bo.id];

							if (!current || ov.versionIndex > current.versionIndex) {
								lastVersionByOrderId[bo.id] = ov;
							}
						});

						// 2. OrderLines de la dernière version uniquement
						const validLines = lines.filter((line) => {
							const ov = line.orderVersion;
							const bo = ov?.businessOrder;

							if (!line.id || !ov || !bo) return false;

							return lastVersionByOrderId[bo.id]?.id === ov.id;
						});

						// Retourner un FetchResult<OrderLineData> conforme attendu par GenericCrudCard
						return { data: validLines, meta: res.meta };
					})
			}
			tableColumns={columns}
			emptyMessage="Aucune ligne de commande trouvée."
			getReturnPath={returnPath ? () => returnPath : undefined}
			returnLabel={returnLabel}
			returnIcon={returnIcon}
			returnInvert={returnInvert}
			size={size}
			icon={faBox}
			className={className}
			extraActions={[
				{
					type: 'custom',
					label: '+ Dépense',
					onClick: (item) => gotoNewExpenseFromQuoteLine(item.id),
				},
			]}
		/>
	);
};

export default OrderLineExpenseAssociationCard;
